/**
 * Test-fixture patient record for Medical History deep links.
 *
 * Several Medical History screens are per-item detail steps: they render the
 * rows the patient picked one screen earlier, and the picks live in the
 * section context, which hydrates them off the patient record the API returns
 * (see `MedicationsVaccinesContext` hydrating `state.selectedVaccines` from
 * `patient.medicalHistory.vaccinations`). A cold deep link to such a step has
 * no picks yet, so the step renders empty or bounces back.
 *
 * Enabled with `?test=1&test_medical_history_record=<key>` on a `__DEV__`
 * build. The fixture supplies one record patch per key, in the shape the
 * patient endpoint returns it — the same fields the section's own
 * `transform*StateToRequest` PATCHes and the same fields its context reads
 * back off the GET. Each section context merges the patch over what
 * `usePatient` returned; everything downstream — hydration, the detail rows,
 * completion — is then computed by product code from that payload, on the code
 * path a real record takes. Without the param the merge returns the record
 * untouched, so there is no second product behaviour to maintain.
 */
import { FAMILY_HISTORY_RECORD_FIXTURES } from "./familyHistoryRecordFixtures";
import { DETAIL_STEP_RECORD_FIXTURES } from "./detailStepRecordFixtures";

export type MedicalHistoryRecordPatch = Record<
  string,
  Record<string, unknown> | unknown[] | null
>;

const RECORD_FIXTURES: Record<string, MedicalHistoryRecordPatch> = {
  ...FAMILY_HISTORY_RECORD_FIXTURES,
  ...DETAIL_STEP_RECORD_FIXTURES,
};

const REQUESTED_RECORD_FIXTURE: MedicalHistoryRecordPatch | null = (() => {
  if (!__DEV__ || typeof window === "undefined") return null;
  try {
    const params = new URLSearchParams(window.location.search);
    if (params.get("test") !== "1") return null;
    const requested = params.get("test_medical_history_record");
    if (!requested) return null;
    return RECORD_FIXTURES[requested] ?? null;
  } catch {
    return null;
  }
})();

// A capture that reaches the screen before the query resolves has no record to
// merge onto. One shared empty base keeps the merged result referentially
// stable across renders, the way a cached query result is.
const NO_PATIENT_RECORD: Record<string, unknown> = {};
const mergedRecords = new WeakMap<object, Record<string, unknown>>();

export function withMedicalHistoryRecordFixture<T>(patient: T): T {
  const fixture = REQUESTED_RECORD_FIXTURE;
  if (!fixture) return patient;

  const base = (patient ?? NO_PATIENT_RECORD) as Record<string, unknown>;
  const cached = mergedRecords.get(base);
  if (cached) return cached as T;

  const next: Record<string, unknown> = { ...base };
  for (const [key, value] of Object.entries(fixture)) {
    const current = base[key];
    next[key] =
      Array.isArray(value) ||
      value === null ||
      typeof current !== "object" ||
      current === null ||
      Array.isArray(current)
        ? value
        : { ...(current as Record<string, unknown>), ...value };
  }

  mergedRecords.set(base, next);
  return next as T;
}
